import { Asset } from 'expo-asset';
import { LinearGradient } from 'expo-linear-gradient';
import Building2 from 'lucide-react-native/icons/building-2';
import Calendar from 'lucide-react-native/icons/calendar';
import CalendarCheck from 'lucide-react-native/icons/calendar-check';
import CalendarDays from 'lucide-react-native/icons/calendar-days';
import CalendarRange from 'lucide-react-native/icons/calendar-range';
import LayoutGrid from 'lucide-react-native/icons/layout-grid';
import MapPin from 'lucide-react-native/icons/map-pin';
import Search from 'lucide-react-native/icons/search';
import Sparkles from 'lucide-react-native/icons/sparkles';
import { StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Dropdown, type DropdownOption } from '@/components/ui/dropdown';
import { Fonts, Radius, Spacing } from '@/constants/theme';
import { useEventsCount } from '@todo/core/hooks/use-events';
import { useFilters } from '@/hooks/use-filters';
import { useTheme } from '@/hooks/use-theme';
import { APP_TZ, dateHeadingPhrase, getDateRange } from '@todo/core/date-range';
import { formatDayMonth } from '@todo/core/i18n/format';
import { useLocale } from '@todo/core/i18n/use-locale';
import { categories, cities, dateFilters } from '@/lib/events-data';

import { CALENDAR_ANCHOR_ID } from './filters-bar.web';
import css from './hero-search.module.css';

const dateIcons = [CalendarRange, CalendarCheck, CalendarDays, Calendar, Calendar];

// Resolved at module load so the URLs are baked into the static-export HTML
// (see the <img> in HeroSearch for why this is a raw <img> and not expo-image).
// Two sizes so narrow/mobile viewports (where the hero renders around
// 700-750px of native pixels, per Lighthouse) don't pay for the full-size
// desktop asset — see the srcSet/sizes on the <img> below.
const HERO_IMAGE_URI = Asset.fromModule(
  require('@/assets/images/riviera/hero-riviera.webp')
).uri;
const HERO_IMAGE_SM_URI = Asset.fromModule(
  require('@/assets/images/riviera/hero-riviera-750.webp')
).uri;

function hexWithAlpha(hex: string, alpha: number) {
  const a = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex}${a}`;
}

// The calendar lives in the FiltersBar sidebar/section, not the hero itself
// — bring it into view once "Pick a date" is chosen (it can be off-screen
// below the hero on mobile, or scrolled past on desktop). This <Dropdown>
// closes its <Modal> (react-native-web) on the same click; that Modal fades
// out for 250ms (Modal/ModalAnimation.js ANIMATION_DURATION) before
// unmounting and refocusing this trigger button for a11y
// (Modal/ModalFocusTrap.js), and the browser's default focus() scrolls that
// trigger back into view — undoing a scroll issued any earlier. So wait for
// that refocus (a `focus` event) before scrolling to the calendar, with a
// fixed-delay fallback in case no such event fires.
function scrollToCalendarAfterDropdownCloses() {
  let done = false;
  const fallback = setTimeout(finish, 600);
  function finish() {
    if (done) return;
    done = true;
    clearTimeout(fallback);
    document.removeEventListener('focus', finish, true);
    requestAnimationFrame(() => {
      document
        .getElementById(CALENDAR_ANCHOR_ID)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }
  document.addEventListener('focus', finish, true);
}

// Web-only: the search bar/title/stats used to pick their layout with a JS
// `isSm`/`isLg` check (see the native hero-search.tsx), which only resolves
// after mount — a visible height jump (stacked search fields -> a single
// row, small title -> large) on top of the page-level width flash that was
// already fixed. Structural spots here (flex-direction, padding, font size)
// are driven by hero-search.module.css media queries instead, resolved by
// the browser at first paint; theme colors stay as regular inline styles.
export function HeroSearch() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const {
    activeCity,
    setActiveCity,
    activeDate,
    setActiveDate,
    customDate,
    setCustomDate,
    activeCategory,
    setActiveCategory,
  } = useFilters();

  const cityOptions: DropdownOption[] = [
    { value: 'all', label: t('common.wholeRegion'), icon: MapPin },
    ...cities.map((c) => ({ value: c.id, label: c.name, icon: MapPin })),
  ];

  const dateOptions: DropdownOption[] = dateFilters.map((d, i) => ({
    value: d.id,
    label: t(`dateFilters.${d.id}`),
    icon: dateIcons[i] ?? Calendar,
  }));

  const categoryOptions: DropdownOption[] = [
    { value: 'all', label: t('common.allCategories'), icon: LayoutGrid },
    ...categories.map((c) => ({ value: c.id, label: t(`categories.${c.id}`), icon: c.icon })),
  ];

  const dateLabel =
    activeDate === 'custom'
      ? formatDayMonth(customDate ?? new Date(), APP_TZ, locale)
      : t(`dateFilters.${activeDate}`);

  const onSelectDate = (id: string) => {
    setActiveDate(id);
    if (id === 'custom') {
      if (!customDate) {
        setCustomDate(new Date());
      }
      scrollToCalendarAfterDropdownCloses();
    }
  };

  const { from, until } = getDateRange(activeDate, customDate);
  const count = useEventsCount({
    city: activeCity === 'all' ? undefined : activeCity,
    category: activeCategory === 'all' ? undefined : activeCategory,
    from,
    until,
  });

  const datePhrase = dateHeadingPhrase(activeDate, customDate, t, locale);
  const countText = count == null ? '—' : String(count);
  const eventsNoun = t('home.eventsNoun', { count: count ?? 0 });
  const eventsStat = `${countText} ${eventsNoun}${datePhrase ? ` ${datePhrase}` : ''}`;

  const stats = [
    { key: 'events', icon: CalendarDays, label: eventsStat },
    { key: 'cities', icon: Building2, label: t('home.statCities', { count: cities.length }) },
    {
      key: 'categories',
      icon: Sparkles,
      label: t('home.statCategories', { count: categories.length }),
    },
  ];

  return (
    <div className={css.section}>
      <View style={styles.imageWrap}>
        {/* Plain <img> (not expo-image) so expo's static export bakes it into
            the server-rendered HTML. expo-image only inserts its <img> after
            hydration (post-2MB-bundle on mobile), which made this LCP element
            paint at ~13s. A raw <img> is discoverable by the preload scanner
            in the initial document and loads in parallel with the JS bundle.
            fetchPriority/eager keep it from being deprioritised or lazied. */}
        <img
          src={HERO_IMAGE_URI}
          srcSet={`${HERO_IMAGE_SM_URI} 750w, ${HERO_IMAGE_URI} 1024w`}
          sizes="(min-width: 1024px) 944px, calc(100vw - 32px)"
          alt=""
          aria-hidden
          className={css.heroImg}
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <LinearGradient
          colors={[
            hexWithAlpha(theme.navy, 0.85),
            hexWithAlpha(theme.navy, 0.55),
            hexWithAlpha(theme.navy, 0.2),
          ]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={StyleSheet.absoluteFill}
          pointerEvents="none"
        />

        <div className={css.content}>
          <div>
            <h1
              className={css.title}
              style={{ color: theme.primaryForeground, fontFamily: Fonts?.serif }}>
              {t('home.heroTitle')}
            </h1>
            <p className={css.subtitle} style={{ color: theme.primaryForeground }}>
              {t('home.heroSubtitle')}
            </p>
          </div>

          <div
            className={css.searchBar}
            style={{ backgroundColor: theme.card, borderColor: theme.border }}>
            <div className={css.selectItem}>
              <Dropdown
                options={cityOptions}
                value={activeCity}
                onChange={setActiveCity}
                icon={MapPin}
                style={styles.selectRow}
                labelStyle={[styles.selectLabel, { color: theme.text }]}
              />
            </div>
            <div className={css.selectItem}>
              <Dropdown
                options={dateOptions}
                value={activeDate}
                onChange={onSelectDate}
                label={dateLabel}
                style={styles.selectRow}
                labelStyle={[styles.selectLabel, { color: theme.text }]}
              />
            </div>
            <div className={css.selectItem}>
              <Dropdown
                options={categoryOptions}
                value={activeCategory}
                onChange={setActiveCategory}
                style={styles.selectRow}
                labelStyle={[styles.selectLabel, { color: theme.text }]}
              />
            </div>
            <div className={css.buttonWrap}>
              <Button
                variant="gold"
                fullWidth
                leadingIcon={<Search size={16} color={theme.goldForeground} />}>
                {t('home.findEvents')}
              </Button>
            </div>
          </div>

          <div className={css.statsRow}>
            {stats.map((s) => (
              <View key={s.key} style={styles.statItem}>
                <s.icon size={14} color={theme.gold} />
                <span className={css.statLabel} style={{ color: theme.primaryForeground }}>
                  {s.label}
                </span>
              </View>
            ))}
          </div>
        </div>
      </View>
    </div>
  );
}

const styles = StyleSheet.create({
  imageWrap: {
    borderRadius: Radius.xl,
    overflow: 'hidden',
    position: 'relative',
  },
  selectRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
    paddingHorizontal: Spacing.two,
    paddingVertical: Spacing.two + 2,
    borderRadius: Radius.md,
  },
  selectLabel: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
});
